/**
 * TAUTAN MEDIA SOSIAL & KONTAK PUBLIK PLATFORM (mig 4150) — pemetaan baris RPC,
 * bentuk formulir, dan validasinya. MURNI: tanpa React, tanpa Supabase, tanpa I/O.
 *
 * Cermin `platformPricing.ts` (mig 3510), dan pembagian validasinya SAMA:
 * SQL hanya menolak yang mustahil (terlalu panjang), sedangkan aturan "URL ini
 * benar-benar profil Instagram" ditegakkan DI SINI — satu fungsi murni, teruji,
 * dipakai formulir setelan DAN pembaca server. Baris yang kelak disunting
 * tangan lewat SQL tidak boleh membuat footer melempar; kolom yang rusak jatuh
 * ke "" (ikon disembunyikan), bukan menjatuhkan halaman.
 *
 * Diuji di `__tests__/platformSocial.test.ts`.
 */

import { isValidPhone } from "@/lib/config";

/**
 * Nomor WhatsApp publik platform — nilai BAWAAN sekaligus nilai seed mig 4150.
 * Dulu konstanta `PLATFORM_WHATSAPP` di `PlatformLanding.tsx`; kini setelan
 * (`public_whatsapp` di `platform_settings`) dengan konstanta ini sebagai
 * cadangan supaya CTA tetap punya tujuan saat Supabase mati.
 *
 * ⚠️ BEDA dari `PLATFORM_SUPPORT_WHATSAPP` (platformSupport.ts) dan itu
 * disengaja: yang itu kontak TEKNIS untuk admin toko yang sudah menyewa,
 * yang ini kontak PENJUALAN untuk calon penyewa. Hari ini nomornya kebetulan
 * sama; menyatukannya berarti mengalihkan salah satunya ikut menyeret yang lain.
 */
export const DEFAULT_PLATFORM_WHATSAPP = "087732735037";

/** Tautan sosial + kontak publik platform. Semua kolom string; "" = tidak
 *  ditampilkan (kecuali `whatsapp`, yang jatuh ke bawaan — CTA tanpa tujuan
 *  lebih buruk daripada ikon yang hilang). */
export type PlatformSocialLinks = {
  instagram: string;
  tiktok: string;
  facebook: string;
  threads: string;
  /** Nomor WA penjualan, format lokal apa adanya (08xx / +62xx). */
  whatsapp: string;
};

/** Nilai bawaan — sosial kosong (belum ada = tidak dipajang), WA ke konstanta.
 *  Harus tetap sama dengan seed mig 4150. */
export const DEFAULT_PLATFORM_SOCIAL: PlatformSocialLinks = {
  instagram: "",
  tiktok: "",
  facebook: "",
  threads: "",
  whatsapp: DEFAULT_PLATFORM_WHATSAPP,
};

/**
 * Host yang sah per kanal. Pencocokan berbasis SUFFIX BATAS-LABEL
 * (`host === d` atau `host` berakhiran `.${d}`) supaya `www.instagram.com`
 * dan `vt.tiktok.com` lolos, sedangkan `evil-instagram.com` maupun
 * `instagram.com.evil.id` tidak.
 */
const SOCIAL_HOSTS: Record<keyof Omit<PlatformSocialLinks, "whatsapp">, string[]> = {
  instagram: ["instagram.com"],
  tiktok: ["tiktok.com"],
  facebook: ["facebook.com", "fb.com"],
  threads: ["threads.net", "threads.com"],
};

export type SocialPlatformKey = keyof typeof SOCIAL_HOSTS;

export const SOCIAL_PLATFORM_KEYS = Object.keys(SOCIAL_HOSTS) as SocialPlatformKey[];

function hostAllowed(host: string, allowed: string[]): boolean {
  const h = host.toLowerCase();
  return allowed.some((d) => h === d || h.endsWith(`.${d}`));
}

/**
 * Normalisasi satu URL profil: terima input tanpa skema ("instagram.com/galea"),
 * paksa https, tolak host di luar kanalnya. `""` bila tidak bisa diselamatkan —
 * ikon yang hilang jauh lebih baik daripada footer yang menautkan `javascript:`
 * atau domain orang lain.
 */
export function normalizeSocialUrl(raw: string, platform: SocialPlatformKey): string {
  const value = (raw ?? "").trim();
  if (!value) return "";
  // Skema selain https ditolak sejak awal — termasuk `javascript:`,`data:`,
  // dan `http:` yang diam-diam kita naikkan hanya bila persis "http://".
  const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value)
    ? value.replace(/^http:\/\//i, "https://")
    : `https://${value}`;
  if (!/^https:\/\//i.test(withScheme)) return "";
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return "";
  }
  if (url.username || url.password) return "";
  if (!hostAllowed(url.hostname, SOCIAL_HOSTS[platform])) return "";
  return url.toString();
}

/**
 * Baris jsonb dari `platform_social_links()` → tautan yang berlaku.
 * DEFENSIF PER-KOLOM (cermin `mapPlatformPricingRow`): satu kolom rusak jatuh
 * ke bawaannya sendiri, bukan membuang seluruh setelan.
 *
 * `null` bila barisnya sendiri tidak ada, supaya pemanggil bisa membedakan
 * "belum dimuat" dari "sudah dimuat dan memang kosong".
 */
export function mapPlatformSocialRow(
  row: Record<string, unknown> | null | undefined
): PlatformSocialLinks | null {
  if (!row || typeof row !== "object") return null;

  const social = (key: SocialPlatformKey): string => {
    const v = row[key];
    return typeof v === "string" ? normalizeSocialUrl(v, key) : "";
  };

  const waRaw = typeof row.whatsapp === "string" ? row.whatsapp.trim() : "";
  return {
    instagram: social("instagram"),
    tiktok: social("tiktok"),
    facebook: social("facebook"),
    threads: social("threads"),
    // Nomor rusak/kosong jatuh ke bawaan — CTA WhatsApp tak boleh kehilangan
    // tujuan hanya karena setelannya salah ketik.
    whatsapp: isValidPhone(waRaw) ? waRaw : DEFAULT_PLATFORM_WHATSAPP,
  };
}

/** Bentuk mentah formulir kartu setelan — semua kolom string apa adanya. */
export type PlatformSocialDraft = {
  instagram: string;
  tiktok: string;
  facebook: string;
  threads: string;
  whatsapp: string;
};

export function socialDraftFrom(links: PlatformSocialLinks): PlatformSocialDraft {
  return { ...links };
}

const SOCIAL_LABELS: Record<SocialPlatformKey, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  threads: "Threads",
};

/**
 * Validasi formulir. `null` bila sah. Kolom sosial boleh kosong (= ikon
 * disembunyikan); yang TERISI wajib bisa dinormalkan ke host kanalnya —
 * menyimpan lalu diam-diam menyembunyikan ikon adalah cara terbaik membuat
 * pemilik mengira fiturnya rusak.
 */
export function validateSocialDraft(draft: PlatformSocialDraft): string | null {
  for (const key of SOCIAL_PLATFORM_KEYS) {
    const raw = (draft[key] ?? "").trim();
    if (raw && !normalizeSocialUrl(raw, key)) {
      return `Tautan ${SOCIAL_LABELS[key]} tidak valid — pakai alamat profil di ${SOCIAL_HOSTS[key][0]}.`;
    }
    if (raw.length > 300) {
      return `Tautan ${SOCIAL_LABELS[key]} terlalu panjang (maks. 300 karakter).`;
    }
  }
  const wa = (draft.whatsapp ?? "").trim();
  if (wa && !isValidPhone(wa)) {
    return "Nomor WhatsApp tidak valid (gunakan 08xx atau +62xx, min. 10 digit).";
  }
  return null;
}

/** Formulir → nilai siap kirim ke RPC: sosial dinormalkan, WA di-trim.
 *  Kolom sosial yang tak lolos normalisasi menjadi "" — tapi jalur simpan
 *  memanggil `validateSocialDraft` dulu, jadi itu hanya terjadi untuk input
 *  yang memang sengaja dikosongkan. */
export function parseSocialDraft(draft: PlatformSocialDraft): PlatformSocialLinks {
  return {
    instagram: normalizeSocialUrl(draft.instagram ?? "", "instagram"),
    tiktok: normalizeSocialUrl(draft.tiktok ?? "", "tiktok"),
    facebook: normalizeSocialUrl(draft.facebook ?? "", "facebook"),
    threads: normalizeSocialUrl(draft.threads ?? "", "threads"),
    whatsapp: (draft.whatsapp ?? "").trim(),
  };
}
